// Opening move: StressDataPlugin - Headless stress data infrastructure for reboot editor
// This plugin registers stress pattern commands and optional auto-fill logic
// Big play: Keeps stress data management separated from rendering concerns (Epic 03)

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  createCommand,
  COMMAND_PRIORITY_EDITOR,
  $getNodeByKey,
  $getRoot,
  $isElementNode,
  type LexicalCommand,
  type LexicalNode,
} from 'lexical'
import type { StressCoordinatorService } from '../services/StressCoordinatorService'
import type { StressPattern } from '../types/prosody'
import {
  $isLyricsTextNode,
  $setStressPattern,
  $clearStressPattern,
  $getStressPattern,
  type LyricsTextNode,
} from '../nodes/LyricsTextNode'

// ============================================================================
// Command Definitions
// ============================================================================

/**
 * Payload for SET_STRESS_PATTERN_COMMAND
 *
 * Main play: Identifies target node and word, provides complete pattern to store
 */
export interface SetStressPatternPayload {
  nodeKey: string
  word: string
  pattern: StressPattern
  /** When true, this is an auto-load (not user action) - skip marking unsaved */
  isAutoFill?: boolean
}

/**
 * Payload for CLEAR_STRESS_PATTERN_COMMAND
 *
 * Main play: Identifies target node and word to clear from state
 */
export interface ClearStressPatternPayload {
  nodeKey: string
  word: string
}

/**
 * SET_STRESS_PATTERN_COMMAND - Store stress pattern in node's NodeState
 *
 * Time to tackle the tricky bit: Updates NodeState which marks node dirty for reconciliation
 * Reference: .local_docs/lexical/core-docs/03-commands.md - Command system patterns
 */
export const SET_STRESS_PATTERN_COMMAND: LexicalCommand<SetStressPatternPayload> =
  createCommand('SET_STRESS_PATTERN_COMMAND')

/**
 * CLEAR_STRESS_PATTERN_COMMAND - Remove stress pattern from node's NodeState
 *
 * Main play: Cleans up stress data when user wants to reset analysis
 */
export const CLEAR_STRESS_PATTERN_COMMAND: LexicalCommand<ClearStressPatternPayload> =
  createCommand('CLEAR_STRESS_PATTERN_COMMAND')

/**
 * Payload for SET_LINE_STRESS_METADATA_COMMAND
 *
 * Epic 05 Scope 02: Stores per-line stress metadata from batch analysis
 * Main play: Maps node keys to aggregated stress metrics for line-level visualization
 * Scope 09: Added expected_stressed_syllables for badge match-quality coloring
 */
export interface SetLineStressMetadataPayload {
  /** Map of node key → line stress counts */
  metadata: Record<
    string,
    {
      total_syllables: number
      stressed_syllables: number
      words: number
      /** Expected stressed syllable count (when known) - drives badge visibility/color */
      expected_stressed_syllables?: number
    }
  >
}

/**
 * SET_LINE_STRESS_METADATA_COMMAND - Store line-level stress metadata
 *
 * Epic 05 Scope 02: Dispatched by StressLineAnalysisPlugin after batch analysis
 * Main play: StressOverlayPlugin consumes this to render per-line stress counts
 * Reference: Acceptance criteria - SET_LINE_STRESS_METADATA_COMMAND tagged with STRESS_LINE_ANALYSIS_TAG
 */
export const SET_LINE_STRESS_METADATA_COMMAND: LexicalCommand<SetLineStressMetadataPayload> =
  createCommand('SET_LINE_STRESS_METADATA_COMMAND')

// ============================================================================
// Epic 07 Commands - Syllable-level stress overrides
// ============================================================================

/**
 * Payload for CYCLE_STRESS_LEVEL_COMMAND
 *
 * Epic 07: Cycles syllable stress level L0 → L1 → L2 → L0
 */
export interface CycleStressLevelPayload {
  nodeKey: string
  word: string
  syllableIndex: number
}

/**
 * Payload for SET_STRESS_LEVEL_COMMAND
 *
 * Epic 07: Sets specific stress level for a syllable
 */
export interface SetStressLevelPayload {
  nodeKey: string
  word: string
  syllableIndex: number
  level: 0 | 1 | 2
}

/**
 * Payload for APPLY_STRESS_PATTERN_COMMAND
 *
 * Epic 07: Applies metrical pattern starting from clicked syllable
 */
export interface ApplyStressPatternPayload {
  nodeKey: string
  word: string
  pattern: 'trochee' | 'iamb' | 'dactyl' | 'anapest'
}

/**
 * Payload for CLEAR_STRESS_OVERRIDE_COMMAND
 *
 * Epic 07: Clears all manual overrides for a word, restoring original analysis
 */
export interface ClearStressOverridePayload {
  nodeKey: string
  word: string
}

/**
 * CYCLE_STRESS_LEVEL_COMMAND - Cycle syllable stress level
 *
 * Epic 07: L0 → L1 → L2 → L0 cycle for user-friendly stress adjustment
 */
export const CYCLE_STRESS_LEVEL_COMMAND: LexicalCommand<CycleStressLevelPayload> =
  createCommand('CYCLE_STRESS_LEVEL_COMMAND')

/**
 * SET_STRESS_LEVEL_COMMAND - Set specific stress level
 *
 * Epic 07: Direct level setting for precise control
 */
export const SET_STRESS_LEVEL_COMMAND: LexicalCommand<SetStressLevelPayload> =
  createCommand('SET_STRESS_LEVEL_COMMAND')

/**
 * APPLY_STRESS_PATTERN_COMMAND - Apply metrical pattern
 *
 * Epic 07: Trochee (10), Iamb (01), Dactyl (100), Anapest (001)
 */
export const APPLY_STRESS_PATTERN_COMMAND: LexicalCommand<ApplyStressPatternPayload> =
  createCommand('APPLY_STRESS_PATTERN_COMMAND')

/**
 * CLEAR_STRESS_OVERRIDE_COMMAND - Clear manual overrides
 *
 * Epic 07: Restores word to original analyzed stress pattern
 */
export const CLEAR_STRESS_OVERRIDE_COMMAND: LexicalCommand<ClearStressOverridePayload> =
  createCommand('CLEAR_STRESS_OVERRIDE_COMMAND')

// ============================================================================
// Feature Flags
// ============================================================================

/**
 * Check if stress auto-fill feature is enabled
 *
 * Opening move: Look up LEXICAL_STRESS_AUTOFILL flag from environment
 * Big play: Checks process.env and window (browser context)
 * Victory lap: Mirrors pattern from RichTextLyricsEditor for consistency
 * NOTE: import.meta.env check removed for Jest compatibility (Vite sets process.env anyway)
 */
function isStressAutofillEnabled(): boolean {
  // Check Node.js process.env (works in both Vite and Jest)
  if (typeof process !== 'undefined' && process.env?.LEXICAL_STRESS_AUTOFILL === 'true') {
    return true
  }

  // Check window for browser runtime injection
  if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).LEXICAL_STRESS_AUTOFILL === 'true') {
    return true
  }

  // Victory lap: Default disabled for safety
  return false
}

/**
 * Update tag to prevent auto-fill listener loops
 *
 * Time to tackle the tricky bit: When auto-fill dispatches SET_STRESS_PATTERN_COMMAND,
 * we tag the update so the listener doesn't trigger recursively
 * Reference: .local_docs/lexical/core-docs/03-commands.md - Update tags and listener loops
 */
export const STRESS_AUTOFILL_TAG = 'stress-autofill'

/**
 * Update tag for backend error scenarios
 *
 * Epic 03: Tags failed updates so overlays can skip stale renders
 * Main play: Prevents auto-fill loops when backend is down
 */
export const STRESS_BACKEND_ERROR_TAG = 'stress-backend-error'

/**
 * Update tag for stress overlay recalculation
 *
 * Epic 04: Signals StressOverlayPlugin to recompute mark positions
 * Main play: When programmatic stress data changes occur, dispatch update with this tag
 * Reference: .local_docs/lexical/core-docs/03-commands.md - Update tags and plugin coordination
 */
export const STRESS_OVERLAY_TAG = 'stress-overlay-recalc'

/**
 * Update tag for contextual stress overrides
 *
 * Epic 05 Scope 02: Tags updates from contextual POS analysis
 * Main play: Distinguishes contextual overrides from dictionary lookups
 * Reference: Acceptance criteria - StressAutoLoadPlugin dispatches UPDATE_STRESS_PATTERN_COMMAND with STRESS_CONTEXTUAL_TAG
 */
const STRESS_CONTEXTUAL_TAG = 'stress-contextual'

/**
 * Update tag for line-level stress metadata
 *
 * Epic 05 Scope 02: Tags updates from StressLineAnalysisPlugin batch analysis
 * Main play: Distinguishes line-level metadata from word-level patterns
 * Reference: Acceptance criteria - StressLineAnalysisPlugin dispatches SET_LINE_STRESS_METADATA_COMMAND with STRESS_LINE_ANALYSIS_TAG
 */
const STRESS_LINE_ANALYSIS_TAG = 'stress-line-analysis'

// Victory lap: Export tags for use by other plugins
export { STRESS_CONTEXTUAL_TAG, STRESS_LINE_ANALYSIS_TAG }

// ============================================================================
// Plugin Component
// ============================================================================

export interface StressDataPluginProps {
  /**
   * StressCoordinatorService instance for analyzing words
   *
   * Main play: DI-friendly design - inject mock for tests, real for production
   */
  coordinator: StressCoordinatorService

  /**
   * Enable auto-fill (optional override)
   *
   * Big play: Allows tests to force-enable/disable regardless of env flags
   * Victory lap: Defaults to environment flag check if not specified
   */
  enableAutoFill?: boolean

  /**
   * Callback when stress patterns are modified (Epic 07)
   *
   * Main play: Allows parent to track dirty state when user overrides stress
   */
  onStressPatternChange?: () => void
}

/**
 * StressDataPlugin - Registers stress pattern command handlers and optional auto-fill
 *
 * Main play: Headless plugin that manages stress data without rendering anything
 * Big play: Commands registered at COMMAND_PRIORITY_EDITOR per iteration plan requirements
 * Time to tackle the tricky bit: Auto-fill batches coordinator calls and guards against loops
 *
 * Critical requirements from iteration plan:
 * - Commands MUST use COMMAND_PRIORITY_EDITOR
 * - Auto-fill MUST be guarded by LEXICAL_STRESS_AUTOFILL flag and STRESS_AUTOFILL_TAG
 * - Commands MUST update NodeState (no direct property mutations)
 *
 * Reference: .local_docs/lexical/core-docs/03-commands.md - Command registration lifecycle
 */
export default function StressDataPlugin({
  coordinator,
  enableAutoFill,
  onStressPatternChange,
}: StressDataPluginProps) {
  const [editor] = useLexicalComposerContext()

  // Opening move: Determine if auto-fill should be active
  const autofillActive = enableAutoFill ?? isStressAutofillEnabled()

  useEffect(() => {
    // ========================================================================
    // Command Handler: SET_STRESS_PATTERN_COMMAND
    // ========================================================================

    const removeSetCommand = editor.registerCommand(
      SET_STRESS_PATTERN_COMMAND,
      ({ nodeKey, word, pattern, isAutoFill }: SetStressPatternPayload) => {
        // Main play: Find target node and update its NodeState
        try {
          const node = $getNodeByKey(nodeKey)

          if (!$isLyricsTextNode(node)) {
            console.warn('SET_STRESS_PATTERN_COMMAND: Node is not LyricsTextNode', nodeKey)
            return false
          }

          // Big play: Use helper to update NodeState (marks node dirty)
          $setStressPattern(node, word, pattern)

          // Epic 07: Notify parent of stress pattern change
          // Skip callback for auto-fill - those aren't user changes and shouldn't mark unsaved
          if (!isAutoFill) {
            onStressPatternChange?.()
          }

          // Victory lap: Command handled successfully
          return true
        } catch (error) {
          console.error('SET_STRESS_PATTERN_COMMAND failed:', error)
          return false
        }
      },
      COMMAND_PRIORITY_EDITOR // CRITICAL: Use EDITOR priority per iteration plan
    )

    // ========================================================================
    // Command Handler: CLEAR_STRESS_PATTERN_COMMAND
    // ========================================================================

    const removeClearCommand = editor.registerCommand(
      CLEAR_STRESS_PATTERN_COMMAND,
      ({ nodeKey, word }: ClearStressPatternPayload) => {
        // Main play: Find target node and clear pattern from NodeState
        try {
          const node = $getNodeByKey(nodeKey)

          if (!$isLyricsTextNode(node)) {
            console.warn('CLEAR_STRESS_PATTERN_COMMAND: Node is not LyricsTextNode', nodeKey)
            return false
          }

          // Big play: Use helper to clear from NodeState
          $clearStressPattern(node, word)

          // Epic 07: Notify parent of stress pattern change
          onStressPatternChange?.()

          // Victory lap: Command handled successfully
          return true
        } catch (error) {
          console.error('CLEAR_STRESS_PATTERN_COMMAND failed:', error)
          return false
        }
      },
      COMMAND_PRIORITY_EDITOR // CRITICAL: Use EDITOR priority per iteration plan
    )

    // ========================================================================
    // Epic 07 Command Handlers - Syllable-level stress overrides
    // ========================================================================

    const removeCycleCommand = editor.registerCommand(
      CYCLE_STRESS_LEVEL_COMMAND,
      ({ nodeKey, word, syllableIndex }: CycleStressLevelPayload) => {
        // Main play: Cycle stress level L0 → L1 → L2 → L0
        try {
          const node = $getNodeByKey(nodeKey)

          if (!$isLyricsTextNode(node)) {
            console.warn('CYCLE_STRESS_LEVEL_COMMAND: Node is not LyricsTextNode', nodeKey)
            return false
          }

          const pattern = $getStressPattern(node, word)
          if (!pattern || !pattern.syllables[syllableIndex]) {
            console.warn('CYCLE_STRESS_LEVEL_COMMAND: No pattern or syllable found', word, syllableIndex)
            return false
          }

          // Big play: Cycle the level
          const currentLevel = pattern.syllables[syllableIndex].stressLevel ?? 0
          const nextLevel = ((currentLevel + 1) % 3) as 0 | 1 | 2

          // Update the syllable
          const updatedSyllables = [...pattern.syllables]
          updatedSyllables[syllableIndex] = {
            ...updatedSyllables[syllableIndex],
            stressLevel: nextLevel,
            stressed: nextLevel > 0,
            overridden: true,
          }

          // Victory lap: Save updated pattern with manual override metadata
          $setStressPattern(node, word, {
            ...pattern,
            syllables: updatedSyllables,
            overridden: true,
            isManualOverride: true,
            overriddenAt: new Date().toISOString(),
          })

          // Epic 07: Notify parent of stress pattern change
          onStressPatternChange?.()

          return true
        } catch (error) {
          console.error('CYCLE_STRESS_LEVEL_COMMAND failed:', error)
          return false
        }
      },
      COMMAND_PRIORITY_EDITOR
    )

    const removeSetLevelCommand = editor.registerCommand(
      SET_STRESS_LEVEL_COMMAND,
      ({ nodeKey, word, syllableIndex, level }: SetStressLevelPayload) => {
        // Main play: Set specific stress level for syllable
        try {
          const node = $getNodeByKey(nodeKey)

          if (!$isLyricsTextNode(node)) {
            console.warn('SET_STRESS_LEVEL_COMMAND: Node is not LyricsTextNode', nodeKey)
            return false
          }

          const pattern = $getStressPattern(node, word)
          if (!pattern || !pattern.syllables[syllableIndex]) {
            console.warn('SET_STRESS_LEVEL_COMMAND: No pattern or syllable found', word, syllableIndex)
            return false
          }

          // Big play: Set the level directly
          const updatedSyllables = [...pattern.syllables]
          updatedSyllables[syllableIndex] = {
            ...updatedSyllables[syllableIndex],
            stressLevel: level,
            stressed: level > 0,
            overridden: true,
          }

          // Victory lap: Save updated pattern with manual override metadata
          $setStressPattern(node, word, {
            ...pattern,
            syllables: updatedSyllables,
            overridden: true,
            isManualOverride: true,
            overriddenAt: new Date().toISOString(),
          })

          // Epic 07: Notify parent of stress pattern change
          onStressPatternChange?.()

          return true
        } catch (error) {
          console.error('SET_STRESS_LEVEL_COMMAND failed:', error)
          return false
        }
      },
      COMMAND_PRIORITY_EDITOR
    )

    const removeApplyPatternCommand = editor.registerCommand(
      APPLY_STRESS_PATTERN_COMMAND,
      ({ nodeKey, word, pattern: metricPattern }: ApplyStressPatternPayload) => {
        // Main play: Apply metrical pattern to word syllables
        try {
          const node = $getNodeByKey(nodeKey)

          if (!$isLyricsTextNode(node)) {
            console.warn('APPLY_STRESS_PATTERN_COMMAND: Node is not LyricsTextNode', nodeKey)
            return false
          }

          const stressPattern = $getStressPattern(node, word)
          if (!stressPattern) {
            console.warn('APPLY_STRESS_PATTERN_COMMAND: No pattern found', word)
            return false
          }

          // Big play: Define metrical patterns (1 = stressed, 0 = unstressed)
          const patterns: Record<string, number[]> = {
            trochee: [1, 0],      // Stressed, Unstressed
            iamb: [0, 1],         // Unstressed, Stressed
            dactyl: [1, 0, 0],    // Stressed, Unstressed, Unstressed
            anapest: [0, 0, 1],   // Unstressed, Unstressed, Stressed
          }

          const sequence = patterns[metricPattern]
          if (!sequence) {
            console.warn('APPLY_STRESS_PATTERN_COMMAND: Unknown pattern', metricPattern)
            return false
          }

          // Time to tackle the tricky bit: Apply pattern cyclically to all syllables
          const updatedSyllables = stressPattern.syllables.map((syl, i) => {
            const level = sequence[i % sequence.length] as 0 | 1
            return {
              ...syl,
              stressLevel: level,
              stressed: level > 0,
              overridden: true,
            }
          })

          // Victory lap: Save updated pattern with manual override metadata
          $setStressPattern(node, word, {
            ...stressPattern,
            syllables: updatedSyllables,
            overridden: true,
            isManualOverride: true,
            overriddenAt: new Date().toISOString(),
          })

          // Epic 07: Notify parent of stress pattern change
          onStressPatternChange?.()

          return true
        } catch (error) {
          console.error('APPLY_STRESS_PATTERN_COMMAND failed:', error)
          return false
        }
      },
      COMMAND_PRIORITY_EDITOR
    )

    const removeClearOverrideCommand = editor.registerCommand(
      CLEAR_STRESS_OVERRIDE_COMMAND,
      ({ nodeKey, word }: ClearStressOverridePayload) => {
        // Main play: Clear overrides AND re-analyze ENTIRE LINE to restore contextual stress
        // The trick: Command handlers must be sync, but re-analysis is async.
        // So we validate sync, return true, then fire off async re-analysis.
        try {
          const node = $getNodeByKey(nodeKey)

          if (!$isLyricsTextNode(node)) {
            console.warn('CLEAR_STRESS_OVERRIDE_COMMAND: Node is not LyricsTextNode', nodeKey)
            return false
          }

          const pattern = $getStressPattern(node, word)
          if (!pattern) {
            console.warn('CLEAR_STRESS_OVERRIDE_COMMAND: No pattern found', word)
            return false
          }

          // Big play: Get the paragraph text for contextual re-analysis
          // Stress patterns are context-dependent (POS tagging matters!)
          const parent = node.getParent()
          const paragraphText = parent?.getTextContent() || word

          // Time to tackle the tricky bit: Use analyzePassage() for full contextual analysis
          // This gives us POS-aware stress patterns, not just dictionary lookups
          coordinator.analyzePassage(paragraphText, 'lyrical').then((passageResult) => {
            // Find the word in the passage results (case-insensitive match)
            const cleanWord = word.toLowerCase().replace(/[^a-z']/g, '')
            const wordAnalysis = passageResult.words.find(
              (w) => w.word.toLowerCase() === cleanWord
            )

            if (!wordAnalysis) {
              console.warn('CLEAR_STRESS_OVERRIDE_COMMAND: Word not found in passage analysis', word)
              return
            }

            // Victory lap: Convert WordAnalysis to StressPattern and apply
            editor.update(
              () => {
                try {
                  const currentNode = $getNodeByKey(nodeKey)
                  if ($isLyricsTextNode(currentNode)) {
                    // Convert backend WordAnalysis to our StressPattern format
                    const freshSyllables = wordAnalysis.syllables.map((syllableText, index) => ({
                      text: syllableText,
                      stressed: wordAnalysis.stress_pattern[index] > 0,
                      confidence: wordAnalysis.confidence,
                      position: index,
                      overridden: false,
                      stressLevel: wordAnalysis.stress_pattern[index] as 0 | 1 | 2,
                      reasoning: wordAnalysis.reasoning,
                    }))

                    $setStressPattern(currentNode, word, {
                      syllables: freshSyllables,
                      overridden: false,
                      isManualOverride: false, // Restored auto pattern, not manual
                      overriddenAt: undefined,
                    })
                  }
                } catch {
                  // Node deleted mid-flight, no sweat
                }
              },
              { tag: STRESS_AUTOFILL_TAG } // Prevent listener loops
            )

            // Epic 07: Notify parent of stress pattern change
            onStressPatternChange?.()
          }).catch((error) => {
            console.error('CLEAR_STRESS_OVERRIDE_COMMAND: Passage re-analysis exception for', word, error)
          })

          return true
        } catch (error) {
          console.error('CLEAR_STRESS_OVERRIDE_COMMAND failed:', error)
          return false
        }
      },
      COMMAND_PRIORITY_EDITOR
    )

    // ========================================================================
    // Auto-Fill Logic (Optional)
    // ========================================================================

    let removeUpdateListener: (() => void) | null = null

    if (autofillActive) {
      // Time to tackle the tricky bit: Batch auto-fill operations to avoid spamming coordinator
      // Main play: Use requestIdleCallback to defer work until browser is idle
      // Victory lap: Tests can flush via setTimeout since jsdom lacks requestIdleCallback

      const scheduleBatch = typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 0)

      // CRITICAL: Use Map with string keys to deduplicate (node, word) pairs
      // Reference: iteration_01_b fix #2 - Set of objects doesn't deduplicate properly
      const pendingBatch: Map<string, { nodeKey: string; word: string }> = new Map()
      let batchScheduled = false

      const processBatch = async () => {
        batchScheduled = false
        const batch = Array.from(pendingBatch.values())
        pendingBatch.clear()

        // Big play: Process all pending word analyses
        for (const { nodeKey, word } of batch) {
          try {
            const result = await coordinator.analyzeWord(word)

            // Epic 03: Guard writes when pattern is null (backend failure)
            if (result.pattern && !result.error) {
              // Time to tackle the tricky bit: Dispatch command with STRESS_AUTOFILL_TAG
              // This prevents the update listener from triggering recursively
              editor.update(
                () => {
                  // Victory lap: Double-check node still exists before updating
                  try {
                    const currentNode = $getNodeByKey(nodeKey)
                    if ($isLyricsTextNode(currentNode)) {
                      $setStressPattern(currentNode, word, result.pattern!)
                    }
                  } catch {
                    // Node was deleted, skip update
                  }
                },
                { tag: [STRESS_AUTOFILL_TAG, 'history-merge'] } // CRITICAL: 'history-merge' tag makes update non-undoable (bypasses undo stack)
              )
            } else if (result.error) {
              // Epic 03: Backend failure - emit instrumentation and tag error update
              console.error(`[LEXICAL_STRESS_ERROR] Auto-fill failed for "${word}": ${result.error}`)

              // Instrumentation hook for debugging (guards against SSR/Jest)
              if (typeof window !== 'undefined' && window.__LEXICAL_DEBUG__) {
                window.__LEXICAL_DEBUG__.record?.({
                  type: 'stress-autofill-error',
                  word,
                  reason: result.error,
                  timestamp: Date.now(),
                })
              }

              // Tag update to prevent retry loops
              editor.update(() => {
                // No-op update, just tags the failure
              }, { tag: STRESS_BACKEND_ERROR_TAG })
            }
          } catch (error) {
            // Unexpected error (not backend-error result)
            const reason = error instanceof Error ? error.message : 'Unknown error'
            console.error(`[LEXICAL_STRESS_ERROR] Auto-fill exception for "${word}": ${reason}`)

            if (typeof window !== 'undefined' && window.__LEXICAL_DEBUG__) {
              window.__LEXICAL_DEBUG__.record?.({
                type: 'stress-autofill-exception',
                word,
                reason,
                timestamp: Date.now(),
              })
            }
          }
        }
      }

      // Opening move: Register update listener for auto-fill
      removeUpdateListener = editor.registerUpdateListener(({ editorState, tags }) => {
        // CRITICAL: Guard against listener loops - skip if update came from auto-fill or backend error
        if (tags.has(STRESS_AUTOFILL_TAG) || tags.has(STRESS_BACKEND_ERROR_TAG)) {
          return
        }

        // Main play: Scan for LyricsTextNodes that might need analysis
        editorState.read(() => {
          // Time to tackle the tricky bit: Walk the tree using public API
          const root = $getRoot()

          // Big play: Recursive function to collect all LyricsTextNodes
          const collectTextNodes = (node: LexicalNode): LyricsTextNode[] => {
            const textNodes: LyricsTextNode[] = []

            if ($isLyricsTextNode(node)) {
              textNodes.push(node)
            }

            // Recursively collect from children (only ElementNodes have children)
            if ($isElementNode(node)) {
              const children = node.getChildren()
              for (const child of children) {
                textNodes.push(...collectTextNodes(child))
              }
            }

            return textNodes
          }

          const textNodes = collectTextNodes(root)

          // Victory lap: For each text node, extract words and check if they need analysis
          for (const node of textNodes) {
            const text = node.getTextContent()
            if (!text.trim()) continue // Skip empty text nodes

            // Opening move: Split text into words (simple whitespace split for now)
            const words = text.split(/\s+/).filter((w) => w.length > 0)

            for (const rawWord of words) {
              // Big play: Normalize word to check if pattern already exists
              const normalizedWord = rawWord.toLowerCase().replace(/[^a-z']/g, '')
              if (!normalizedWord) continue // Skip pure punctuation

              // Time to tackle the tricky bit: Check if this word already has stress data
              const existingPattern = $getStressPattern(node, normalizedWord)

              if (!existingPattern) {
                // Victory lap: No pattern exists, add to pending batch with keyed entry
                // CRITICAL: Use ${nodeKey}:${word} key to deduplicate properly
                const batchKey = `${node.getKey()}:${normalizedWord}`
                pendingBatch.set(batchKey, { nodeKey: node.getKey(), word: normalizedWord })
              }
            }
          }
        })

        // Big play: Schedule batch processing if we have pending work
        if (pendingBatch.size > 0 && !batchScheduled) {
          batchScheduled = true
          scheduleBatch(processBatch)
        }
      })
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    return () => {
      removeSetCommand()
      removeClearCommand()
      removeCycleCommand()
      removeSetLevelCommand()
      removeApplyPatternCommand()
      removeClearOverrideCommand()
      if (removeUpdateListener) {
        removeUpdateListener()
      }
    }
  }, [editor, coordinator, autofillActive, onStressPatternChange])

  // Victory lap: Headless plugin returns null (no UI)
  return null
}

// Victory lap: StressDataPlugin complete with command handlers and auto-fill foundation
// Commands use COMMAND_PRIORITY_EDITOR, auto-fill guards against loops with STRESS_AUTOFILL_TAG
// Ready for Epic 02 integration and testing
